import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { usePageReady } from '../../context/PageReadyContext';
import { waitForImage } from '../../utils/waitForImage';

const preloaded = new Set();

const preloadImage = (src) => {
  if (!src || preloaded.has(src)) return;
  preloaded.add(src);
  const img = new Image();
  img.src = src;
};

/**
 * High-priority hero background using <img> (better LCP than CSS background-image).
 */
const HeroBackground = ({
  src,
  alt = '',
  priority = false,
  gradient = 'linear-gradient(135deg, rgba(37, 99, 235, 0.8) 0%, rgba(59, 130, 246, 0.7) 50%, rgba(139, 92, 246, 0.6) 100%)',
  sx = {},
  children,
}) => {
  const { signalHeroReady, getGeneration } = usePageReady();

  useEffect(() => {
    if (priority && src) preloadImage(src);
  }, [priority, src]);

  useEffect(() => {
    if (!src) return undefined;

    let cancelled = false;
    const generation = getGeneration();

    waitForImage(src).then(() => {
      if (!cancelled && getGeneration() === generation) {
        signalHeroReady();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src, getGeneration, signalHeroReady]);

  const handleImageReady = () => {
    signalHeroReady();
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {src && (
        <Box
          component="img"
          src={src}
          alt={alt}
          fetchPriority={priority ? 'high' : 'auto'}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleImageReady}
          onError={handleImageReady}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: gradient,
        }}
      />
      {children}
    </Box>
  );
};

export { preloadImage };
export default HeroBackground;
